import React, { useMemo } from 'react';
import {
  Autocomplete,
  Checkbox,
  Chip,
  createFilterOptions,
} from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import StyledTextField from '../StyledTextField';
import { COLORS, FONT_WEIGHTS } from '../../theme/constants';

const SELECT_ALL_OPTION = {
  __selectAll: true,
  label: 'Select All',
  value: '__select_all__',
};

const defaultFilterOptions = createFilterOptions();
const checkboxIcon = <CheckBoxOutlineBlank fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const isSelectAllOption = option => !!option?.__selectAll;

const resolveMultiSelectChange = (selectedItems, allOptions) => {
  const withoutSelectAll = (selectedItems || []).filter(
    item => !isSelectAllOption(item)
  );
  if (!(selectedItems || []).some(isSelectAllOption)) {
    return withoutSelectAll;
  }
  const allSelected =
    allOptions.length > 0 && withoutSelectAll.length === allOptions.length;
  return allSelected ? [] : allOptions;
};

/**
 * StyledAutocomplete — Autocomplete + StyledTextField.
 *
 * Optional `selectAll` (multi only): adds Select All / Clear All at the top
 * of the dropdown so multi-select behavior stays consistent app-wide.
 */
const StyledAutocomplete = ({
  label,
  placeholder,
  required = false,
  error = false,
  helperText,
  size = 'small',
  multiple = false,
  selectAll = false,
  showCheckboxes = false,
  options = [],
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  filterOptions,
  renderOption,
  renderTags,
  renderInput,
  disableCloseOnSelect,
  textFieldProps = {},
  ...props
}) => {
  const showSelectAll = multiple && selectAll && options.length > 0;

  const selectedValues = useMemo(() => {
    if (!multiple) return value ?? null;
    return Array.isArray(value) ? value : [];
  }, [multiple, value]);

  const allSelected =
    showSelectAll &&
    Array.isArray(selectedValues) &&
    selectedValues.length === options.length;

  const resolveLabel = option => {
    if (isSelectAllOption(option)) {
      return allSelected ? 'Clear All' : 'Select All';
    }
    if (getOptionLabel) return getOptionLabel(option);
    if (option == null) return '';
    if (typeof option === 'string') return option;
    return option.label ?? option.text ?? option.name ?? String(option.value ?? '');
  };

  const resolveEqual = (option, val) => {
    if (isSelectAllOption(option) || isSelectAllOption(val)) return false;
    if (isOptionEqualToValue) return isOptionEqualToValue(option, val);
    if (option === val) return true;
    if (option?.value != null && val?.value != null) {
      return option.value === val.value;
    }
    return resolveLabel(option) === resolveLabel(val);
  };

  const autocompleteOptions = showSelectAll
    ? [SELECT_ALL_OPTION, ...options]
    : options;

  const resolvedFilterOptions = (opts, state) => {
    if (!showSelectAll) {
      return (filterOptions || defaultFilterOptions)(opts, state);
    }
    const realOptions = opts.filter(option => !isSelectAllOption(option));
    const filtered = (filterOptions || defaultFilterOptions)(realOptions, state);
    return filtered.length > 0 ? [SELECT_ALL_OPTION, ...filtered] : filtered;
  };

  const handleChange = (event, nextValue, reason, details) => {
    if (!onChange) return;
    if (showSelectAll) {
      onChange(
        event,
        resolveMultiSelectChange(nextValue, options),
        reason,
        details
      );
      return;
    }
    onChange(event, nextValue, reason, details);
  };

  const defaultRenderOption = (optionProps, option, state) => {
    if (isSelectAllOption(option)) {
      return (
        <li
          {...optionProps}
          key="__select_all__"
          style={{
            ...optionProps.style,
            fontWeight: FONT_WEIGHTS.semiBold,
            borderBottom: `1px solid ${COLORS.border.light}`,
          }}
        >
          {showCheckboxes && (
            <Checkbox
              icon={checkboxIcon}
              checkedIcon={checkboxCheckedIcon}
              style={{ marginRight: 8 }}
              checked={allSelected}
              indeterminate={
                Array.isArray(selectedValues) &&
                selectedValues.length > 0 &&
                !allSelected
              }
            />
          )}
          {resolveLabel(option)}
        </li>
      );
    }

    return (
      <li {...optionProps} key={option?.value ?? resolveLabel(option)}>
        {showCheckboxes && multiple && (
          <Checkbox
            icon={checkboxIcon}
            checkedIcon={checkboxCheckedIcon}
            style={{ marginRight: 8 }}
            checked={state.selected}
          />
        )}
        {resolveLabel(option)}
      </li>
    );
  };

  const defaultRenderTags = (tagValue, getTagProps) =>
    tagValue
      .filter(option => !isSelectAllOption(option))
      .map((option, index) => (
        <Chip
          size="small"
          label={resolveLabel(option)}
          {...getTagProps({ index })}
          key={option?.value ?? resolveLabel(option)}
        />
      ));

  return (
    <Autocomplete
      multiple={multiple}
      size={size}
      options={autocompleteOptions}
      value={selectedValues}
      onChange={handleChange}
      getOptionLabel={resolveLabel}
      isOptionEqualToValue={resolveEqual}
      filterOptions={resolvedFilterOptions}
      disableCloseOnSelect={
        disableCloseOnSelect ?? (multiple ? true : undefined)
      }
      renderOption={renderOption || defaultRenderOption}
      renderTags={renderTags || (multiple ? defaultRenderTags : undefined)}
      renderInput={
        renderInput ||
        (params => (
          <StyledTextField
            {...params}
            label={label}
            placeholder={placeholder}
            required={required}
            error={error}
            helperText={helperText}
            size={size}
            {...textFieldProps}
          />
        ))
      }
      {...props}
    />
  );
};

export default StyledAutocomplete;
